/**
 * depotGeo — moteur géographique des dépôts (Chantier Dépôts, Phase 1).
 *
 * Design validé avec l'utilisateur (13/06/2026) :
 *   - Chaque dépôt est ancré dans une ville réelle (MAP_CITIES, GPS).
 *   - Rayon d'action par dépôt : 250 km de base (extensible Phase 3).
 *   - Chaque contrat naît dans une ville d'origine ; s'il est hors du rayon
 *     de TOUS vos dépôts → pénalité de revenus variable ≥ 40 % (sous-
 *     traitance locale, trajets à vide, péages) qui croît avec la distance.
 *   - ~30 % des contrats spawnment hors zone, dont des opportunités rares
 *     très lucratives (+30-50 %) : la carotte pour ouvrir de nouveaux
 *     dépôts ou racheter un concurrent implanté là-bas.
 *   - Loyers par ville (cible ~8-9 % du CA) : la grille évolue avec les
 *     investissements (taille du terrain). Aides à l'implantation : QG
 *     exonéré 90 j (pépinière), satellites −50 % pendant 180 j + subvention
 *     régionale de 30 % du coût d'ouverture.
 *   - 6 dépôts max en France (3 ouverts + 3 rachetés) + 2 slots
 *     internationaux (villes UE, phase 4).
 */
import { MAP_CITIES, findCityById, haversineKm } from './mapCities';
import { getDepotEffectiveInvestments } from './depotPhases';

/* ── Villes & loyers ─────────────────────────────────────────────────── */

// Tier A : métropoles premium · Tier B : grandes métropoles · Tier C :
// villes moyennes · EU : implantations internationales.
const CITY_TIER_MAP = {
  paris: 'A', lyon: 'A', marseille: 'A',
  bordeaux: 'B', lille: 'B', toulouse: 'B', nantes: 'B', strasbourg: 'B', rennes: 'B',
  nice: 'C', montpellier: 'C', clermont: 'C', dijon: 'C',
};

export const RENT_BY_TIER = { A: 4500, B: 3500, C: 2500, EU: 5500 };

export function getCityTier(cityId) {
  if (CITY_TIER_MAP[cityId]) return CITY_TIER_MAP[cityId];
  const city = findCityById(cityId);
  if (city && city.country !== 'FR') return 'EU';
  return 'C';
}

export function getCityBaseRent(cityId) {
  return RENT_BY_TIER[getCityTier(cityId)] || RENT_BY_TIER.C;
}

/* ── Dépôts : ville, rayon ───────────────────────────────────────────── */

export const DEPOT_BASE_RADIUS_KM = 250;
export const DEPOT_RADIUS_UPGRADE_KM = 100; // Phase 3 : extension logistique

// Villes d'origine des concurrents M&A — racheter = couvrir leur zone.
export const COMPETITOR_HOME_CITIES = {
  transnord: 'lille',
  logistar: 'strasbourg',
  ecoroute: 'nantes',
  rapidfret: 'toulouse',
};

/**
 * Ville d'ancrage d'un dépôt, robuste aux sauvegardes antérieures au
 * chantier géo : QG → ville de création de la société ; dépôt racheté →
 * ville du concurrent (par brand) ; sinon fallback Dijon (tier C neutre).
 */
export function getDepotCityId(depot, company) {
  if (!depot) return company?.city || 'paris';
  if (depot.cityId) return depot.cityId;
  if (depot.isHeadquarters) return company?.city || 'paris';
  if (depot.brand && COMPETITOR_HOME_CITIES[depot.brand]) return COMPETITOR_HOME_CITIES[depot.brand];
  return 'dijon';
}

export function getDepotRadiusKm(depot, activeInvestments = null) {
  let radius = DEPOT_BASE_RADIUS_KM;
  // 13/07/2026 — Progression unifiée : l'extension logistique d'un dépôt
  // secondaire passe par l'investissement `inv-depot-06` acheté POUR ce
  // dépôt (les anciennes évolutions `radiusExtension` des sauvegardes
  // legacy sont mappées par getDepotEffectiveInvestments).
  if (depot && !depot.isHeadquarters && getDepotEffectiveInvestments(depot)['inv-depot-06']) {
    radius += DEPOT_RADIUS_UPGRADE_KM;
  }
  // 17/06/2026 — Le QG bénéficie d'un investissement équivalent
  // (`inv-depot-06`, géré côté investissements globaux) qui étend
  // son rayon de +100 km. Le param `activeInvestments` est optionnel
  // pour préserver l'API rétrocompatible (les callers historiques
  // continuent de fonctionner ; seul le QG bénéficie de l'extension
  // quand l'investissement est passé).
  if (depot?.isHeadquarters && activeInvestments && activeInvestments['inv-depot-06']) {
    radius += DEPOT_RADIUS_UPGRADE_KM;
  }
  return radius;
}

/* ── Loyers ──────────────────────────────────────────────────────────── */

export const HQ_RENT_FREE_DAYS = 90;          // pépinière d'entreprises
export const SATELLITE_RENT_AID_DAYS = 180;   // aide régionale −50 %
export const SATELLITE_RENT_AID_RATE = 0.5;
export const OPEN_DEPOT_SUBSIDY_RATE = 0.30;  // subvention à l'implantation
export const MAX_INTERNATIONAL_DEPOTS = 2;
// 17/06/2026 (Lot 4) — Loyer évolutif (demande user) : chaque évolution
// agrandit le site et fait grimper le loyer mensuel. Pour qu'un dépôt
// totalement upgradé reste pédagogiquement borné, on plafonne à 20 K€/mois.
export const PER_UPGRADE_RENT_FACTOR = 0.55;  // +55 % de la base ville / évolution
export const MAX_DEPOT_RENT_PER_MONTH = 20000;

/**
 * Facteur « taille du terrain » du QG — le loyer évolue avec les
 * investissements (demande utilisateur). Acheter le terrain (inv-depot-03)
 * réduit le loyer au foncier résiduel (taxes + charges).
 */
export function getHqRentFactor(activeInvestments = {}) {
  if (activeInvestments['inv-depot-03']) return 0.10; // terrain acheté
  let factor = 0.25;                                   // petit local de départ
  if (activeInvestments['inv-depot-01']) factor += 0.20; // agrandissement x2
  if (activeInvestments['inv-depot-02']) factor += 0.20; // entrepôt dédié
  if (activeInvestments['inv-depot-04']) factor += 0.10; // quai de chargement
  if (activeInvestments['inv-depot-05']) factor += 0.10; // zone frigorifique
  // 09/02/2026 — Q2 Lot E : panneaux photovoltaïques QG (inv-depot-solar)
  // → loyer -20 % (autoconsommation). Cohérent avec depot.upgrades.solar
  // côté satellite (cf. getDepotRent).
  if (activeInvestments['inv-depot-solar']) factor *= 0.80;
  return factor;
}

/**
 * Loyer mensuel d'un dépôt donné.
 *   QG        : base ville × facteur terrain — exonéré 90 premiers jours.
 *   Satellite : base ville × (0,8 + évolutions × PER_UPGRADE_RENT_FACTOR)
 *               — aide régionale −50 % pendant 180 j.
 *   Racheté   : base ville × (1,0 + évolutions × PER_UPGRADE_RENT_FACTOR).
 *   International (ville non FR) : surcoût ×1,25.
 *   Cap : MAX_DEPOT_RENT_PER_MONTH (20 K€/mois) appliqué après tout.
 */
export function getDepotRent(depot, activeInvestments = {}, company = null, totalDays = 0) {
  const cityId = getDepotCityId(depot, company);
  const city = findCityById(cityId);
  let rent = getCityBaseRent(cityId);
  if (depot?.isHeadquarters) {
    if (totalDays < HQ_RENT_FREE_DAYS) return 0;
    rent *= getHqRentFactor(activeInvestments);
  } else {
    // 17/06/2026 — chaque évolution active grossit le site → +55 % de la
    // base ville. 13/07/2026 — Progression unifiée : le compteur lit les
    // investissements effectifs du dépôt (unifiés + legacy mappés), MOINS
    // les jalons hérités au rachat (`seedInvestmentCount`) : la taille du
    // site au closing est déjà payée dans le prix d'acquisition — seules
    // les évolutions financées ENSUITE par le joueur font grimper le loyer.
    const effInv = getDepotEffectiveInvestments(depot);
    const upgCount = Math.max(
      0, Object.keys(effInv).length - (depot?.seedInvestmentCount || 0),
    );
    const baseFactor = depot?.acquired ? 1.0 : 0.8;
    rent *= baseFactor + upgCount * PER_UPGRADE_RENT_FACTOR;
    if (!depot?.acquired) {
      const age = totalDays - (depot?.openedDay || 0);
      if (age < SATELLITE_RENT_AID_DAYS) rent *= SATELLITE_RENT_AID_RATE;
    }
    // 14/06/2026 — panneaux photovoltaïques : énergie autoconsommée
    // revendue au bailleur → loyer du site −20 % (inv-depot-solar).
    if (effInv['inv-depot-solar']) rent *= 0.80;
  }
  if (city && city.country !== 'FR') rent *= 1.25;
  // 17/06/2026 — cap pédagogique pour ne pas exploser au-delà de la
  // visibilité user (un dépôt fully upgradé international hit ~30 K€ sinon).
  if (!depot?.isHeadquarters && rent > MAX_DEPOT_RENT_PER_MONTH) {
    rent = MAX_DEPOT_RENT_PER_MONTH;
  }
  return Math.round(rent);
}

export function getTotalDepotRent(ownedDepots = [], activeInvestments = {}, company = null, totalDays = 0) {
  return (ownedDepots || []).reduce(
    (sum, d) => sum + getDepotRent(d, activeInvestments, company, totalDays), 0,
  );
}

/* ── Couverture géographique des contrats ────────────────────────────── */

export const OUT_OF_ZONE_BASE_PENALTY = 0.40; // ≥ 40 % (choix utilisateur)
export const OUT_OF_ZONE_MAX_PENALTY = 0.70;

/**
 * Évalue la couverture d'un contrat par le réseau de dépôts.
 * Contrats sans ville d'origine (sauvegardes antérieures, tests legacy) →
 * considérés couverts (pas de gate rétroactif).
 *
 * @param {Object} [activeInvestments] — facultatif. Si fourni et que
 *   `inv-depot-06` (Extension logistique +100 km) est actif, le rayon du
 *   QG est étendu de +100 km dans le calcul de couverture.
 *
 * @returns {{ covered: boolean, distanceKm: number, nearestDepotId: string|null,
 *             nearestDepotCityId: string|null, penalty: number }}
 */
export function evaluateContractCoverage(contract, ownedDepots = [], company = null, activeInvestments = null) {
  const none = { covered: true, distanceKm: 0, nearestDepotId: null, nearestDepotCityId: null, penalty: 0 };
  if (!contract || !contract.originCityId) return none;
  const origin = findCityById(contract.originCityId);
  if (!origin || !(ownedDepots || []).length) return none;

  let best = null;
  ownedDepots.forEach(depot => {
    const cityId = getDepotCityId(depot, company);
    const city = findCityById(cityId);
    if (!city) return;
    const d = haversineKm(city, origin);
    const radius = getDepotRadiusKm(depot, activeInvestments);
    if (!best || d - radius < best.excess) {
      best = { depot, cityId, distanceKm: d, radius, excess: d - radius };
    }
  });
  if (!best) return none;

  const covered = best.distanceKm <= best.radius;
  // Pénalité variable : 40 % au bord du rayon, +5 % par 100 km au-delà,
  // plafonnée à 70 % (sous-traitance quasi totale).
  const penalty = covered
    ? 0
    : Math.min(
        OUT_OF_ZONE_MAX_PENALTY,
        OUT_OF_ZONE_BASE_PENALTY + ((best.distanceKm - best.radius) / 100) * 0.05,
      );
  return {
    covered,
    distanceKm: best.distanceKm,
    nearestDepotId: best.depot.id,
    nearestDepotCityId: best.cityId,
    penalty: Math.round(penalty * 100) / 100,
  };
}

/**
 * 13/06/2026 — Phase 3 : un contrat couvert ne peut être servi que par un
 * véhicule rattaché à un dépôt qui couvre sa ville d'origine.
 *
 * @param {Object} [activeInvestments] — propage l'extension logistique
 *   du QG (`inv-depot-06`) sur le rayon utilisé pour la vérification.
 */
export function depotCoversContract(depot, contract, company = null, activeInvestments = null) {
  if (!contract || !contract.originCityId || !depot) return true;
  const city = findCityById(getDepotCityId(depot, company));
  const origin = findCityById(contract.originCityId);
  if (!city || !origin) return true;
  return haversineKm(city, origin) <= getDepotRadiusKm(depot, activeInvestments);
}

/** Coût de transfert d'un camion entre deux dépôts (1,2 €/km, min 300 €). */
export function getVehicleTransferCost(fromDepot, toDepot, company = null) {
  const a = findCityById(getDepotCityId(fromDepot, company));
  const b = findCityById(getDepotCityId(toDepot, company));
  if (!a || !b) return 300;
  return Math.max(300, Math.round(haversineKm(a, b) * 1.2));
}

/* ── Spawn géographique des contrats ─────────────────────────────────── */

export const FAR_CONTRACT_SPAWN_RATE = 0.30;
export const FAR_CONTRACT_BOOST_MIN = 1.30;
export const FAR_CONTRACT_BOOST_SPAN = 0.20; // 1.30 → 1.50

/**
 * Choisit la ville d'origine d'un nouveau contrat.
 *   ~70 % : ville couverte par un de vos dépôts (poids fort sur les villes
 *           de dépôt elles-mêmes),
 *   ~30 % : ville hors zone (opportunité rare, boost de revenus +30-50 %).
 * Gating par phase de jeu (les villes UE n'apparaissent qu'en phase 4).
 *
 * @returns {{ originCityId: string|null, far: boolean }}
 */
export function pickContractOriginCity(ownedDepots = [], company = null, phase = 1, rng = Math.random, options = {}) {
  const depotCities = (ownedDepots || [])
    .map(d => ({ city: findCityById(getDepotCityId(d, company)), radius: getDepotRadiusKm(d) }))
    .filter(x => x.city);
  if (depotCities.length === 0) {
    return { originCityId: company?.city || null, far: false };
  }
  const isExport = options.isExport || false;
  const phaseCities = MAP_CITIES.filter(c => {
    if (c.phase > Math.max(phase, 1)) return false;
    if (isExport) return true; // Export : toutes villes débloquées éligibles
    return c.country === 'FR';
  });
  const covered = [];
  const uncovered = [];
  phaseCities.forEach(c => {
    const isCov = depotCities.some(dc => haversineKm(dc.city, c) <= dc.radius);
    (isCov ? covered : uncovered).push(c);
  });
  const goFar = uncovered.length > 0 && rng() < FAR_CONTRACT_SPAWN_RATE;
  let pool = goFar ? uncovered : covered;
  if (pool.length === 0) pool = phaseCities;
  if (pool.length === 0) return { originCityId: company?.city || null, far: false };

  // Poids ×3 sur les villes de dépôt (l'activité gravite autour des sites).
  const weighted = [];
  pool.forEach(c => {
    const isDepotCity = depotCities.some(dc => dc.city.id === c.id);
    const w = !goFar && isDepotCity ? 3 : 1;
    for (let i = 0; i < w; i++) weighted.push(c);
  });
  const city = weighted[Math.floor(rng() * weighted.length)];
  return { originCityId: city?.id || null, far: goFar };
}
